import { createRoute, Link } from '@tanstack/react-router'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MutedText,
  Stack,
} from '@volee/ui'

import { ResetConfirmForm, ResetRequestForm } from '../components/password-reset-form.tsx'
import { usePasswordReset } from '../data/use-password-reset.ts'
import { messages } from '../messages/index.ts'
import { parseReturnTo } from './params.ts'
import { rootRoute } from './root.tsx'
import { signInLink } from './sign-in.tsx'

// Password forgotten, in re-enterable steps: why this IS a route while the first access is a state
// of /sign-in (apps/admin-panel/docs/login-sta-dentro-il-prodotto.md). No email in the URL.

export interface ForgotPasswordSearch {
  readonly returnTo: string
}

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  validateSearch: (search: Record<string, unknown>): ForgotPasswordSearch => ({
    returnTo: parseReturnTo(search.returnTo),
  }),
  // Reached without the back office being usable: no frame around it (see `chrome`).
  staticData: { chrome: 'bare' },
  component: ForgotPassword,
})

/** The three steps name themselves, so the card does not have to read two flags inline. */
function headingFor(step: { delivery: boolean; changed: boolean }) {
  if (step.changed) {
    return {
      title: messages.passwordReset.done,
      description: messages.passwordReset.signInYourself,
    }
  }
  if (step.delivery) {
    return {
      title: messages.passwordReset.codeTitle,
      description: messages.passwordReset.codeDescription,
    }
  }
  return { title: messages.passwordReset.title, description: messages.passwordReset.description }
}

function ForgotPassword() {
  const { returnTo } = forgotPasswordRoute.useSearch()
  const { delivery, request, confirm, changed } = usePasswordReset(returnTo)

  function body() {
    if (delivery === null) return <ResetRequestForm {...request} />
    // Reached only when the sign-in that follows failed: on success this page is already gone.
    if (changed) {
      return (
        <Alert variant="info">
          <AlertDescription>{messages.passwordReset.signInYourself}</AlertDescription>
        </Alert>
      )
    }
    return <ResetConfirmForm {...confirm} />
  }

  // The step owns its own heading: the two forms used to carry a second copy of it.
  const heading = headingFor({ delivery: delivery !== null, changed })

  return (
    <section className="mx-auto flex max-w-sm flex-col justify-center py-10">
      <Card>
        <CardHeader>
          <CardTitle>{heading.title}</CardTitle>
          <MutedText>{heading.description}</MutedText>
        </CardHeader>
        <CardContent>
          <Stack gap="md">
            {body()}
            <Button variant="ghost" asChild>
              <Link {...signInLink}>{messages.passwordReset.backToSignIn}</Link>
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </section>
  )
}
